import { X, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "./button";
import { motion } from "framer-motion";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { miniAppBridgeService } from "@/api/services";

interface ImageViewerProps {
  image: string;
  onClose: () => void;
  messageId?: string;
  imageIndex?: number;
  /**
   * Kaynak küçük resmin en/boy oranı (naturalWidth / naturalHeight).
   * Paylaşımlı geçişte hedef kutu baştan doğru oranda açılır; verilmezse
   * görsel yüklenince ölçülür.
   */
  aspectRatio?: number;
}

/** Görsel kutusu ekrana bu paylarla sığdırılır */
const VIEWPORT_PAD = 24;
const MAX_HEIGHT_RATIO = 0.9;

/** Oranı koruyarak pencereye sığan en büyük kutu */
function fitToViewport(aspect: number, vw: number, vh: number) {
  const maxW = vw - VIEWPORT_PAD * 2;
  const maxH = vh * MAX_HEIGHT_RATIO;
  let w = maxW;
  let h = w / aspect;
  if (h > maxH) {
    h = maxH;
    w = h * aspect;
  }
  return { width: Math.round(w), height: Math.round(h) };
}

export function ImageViewer({
  image,
  onClose,
  messageId,
  imageIndex,
  aspectRatio,
}: ImageViewerProps) {
  const { t } = useTranslation();
  const { success: successToast, error: errorToast } = useToast();

  // Paylaşımlı geçiş için hedef kutu: oran prop'tan ya da görselden gelir
  const [aspect, setAspect] = useState<number | null>(aspectRatio ?? null);
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  useEffect(() => {
    if (aspect) return;
    const probe = new Image();
    probe.onload = () => {
      if (probe.naturalWidth && probe.naturalHeight) {
        setAspect(probe.naturalWidth / probe.naturalHeight);
      }
    };
    probe.src = image;
  }, [aspect, image]);
  useEffect(() => {
    const onResize = () =>
      setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ESC tuşu ile kapatma ve body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    // Lock body scroll when viewer is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const handleDownload = async () => {
    try {
      // Detect file extension from URL
      const urlLower = image.toLowerCase();
      let extension = "png"; // default
      if (urlLower.includes(".jpg") || urlLower.includes(".jpeg"))
        extension = "jpg";
      else if (urlLower.includes(".gif")) extension = "gif";
      else if (urlLower.includes(".webp")) extension = "webp";
      else if (urlLower.includes(".svg")) extension = "svg";

      // Fetch the file as blob
      const response = await fetch(image);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `image-${Date.now()}.${extension}`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      successToast(t("wizard.downloadStarted") || "İndirme başlatıldı");
    } catch (errorResponse: any) {
      console.error("Download failed:", errorResponse);
      // Fallback: Open in new tab
      window.open(image, "_blank");
      errorToast(t("wizard.downloadFailed") || "İndirme başarısız");
    }
  };

  const fileName = image.split("/").pop() || "image.png";
  const layoutId =
    messageId && imageIndex !== undefined
      ? `${messageId}-image-${imageIndex}`
      : undefined;
  // Paylaşımlı geçiş: kaynak kare/kırpık, hedef tam oran. Kutu (layoutId)
  // kaynaktan hedefe morf olur, içindeki görsel `object-cover` + `layout` ile
  // hep kutuyu doldurur; oran değişirken kırpma azalır, esneme olmaz.
  const shared = Boolean(layoutId) && aspect !== null;
  const box = shared ? fitToViewport(aspect!, viewport.w, viewport.h) : null;

  // Portal kullanarak modal'ı document.body'ye render et.
  // Çıkış animasyonu çağıranın <AnimatePresence> sarmasıyla çalışır.
  const modalContent = (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[99999] bg-background/90 backdrop-blur-md"
        onClick={onClose}
        style={{ 
          pointerEvents: 'auto',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
      >
        <div 
          className="fixed inset-0 flex items-center justify-center p-4 sm:p-6"
          onClick={(e) => {
            // Close when clicking outside content
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            // Paylaşımlı geçişte kutu zaten hareket ediyor; ayrıca ölçekleme
            // iki animasyonu çakıştırıp titretir
            initial={shared ? false : { scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={shared ? undefined : { scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0.2 }}
            className="relative max-w-full max-h-full pointer-events-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="secondary"
              size="icon"
              className="absolute top-4 right-4 z-50 rounded-full h-8 w-8 shadow-lg border border-border bg-background hover:bg-accent"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
            >
              <X className="h-5 w-5 text-foreground" />
            </Button>
            {shared && box ? (
              <motion.div
                layoutId={layoutId}
                style={{ width: box.width, height: box.height }}
                className="overflow-hidden rounded-xl bg-gray-100 dark:bg-gray-800"
                transition={{ type: "spring", duration: 0.55, bounce: 0.12 }}
              >
                <motion.img
                  layout
                  src={image}
                  alt="Full size"
                  draggable={false}
                  className="h-full w-full object-cover"
                  transition={{ type: "spring", duration: 0.55, bounce: 0.12 }}
                />
              </motion.div>
            ) : (
              <div className="overflow-auto">
                <img
                  src={image}
                  alt="Full size"
                  className="rounded-lg object-contain max-h-[90vh]"
                />
              </div>
            )}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 bg-gradient-to-t from-black/80 via-black/60 to-transparent dark:from-black/80 dark:via-black/60 dark:to-transparent"
            >
              <div className="flex items-center justify-between max-w-[90%] mx-auto gap-4">
                <span className="text-sm font-medium text-white/90 dark:text-white/90 truncate">
                  {fileName}
                </span>
                {!miniAppBridgeService.isInMiniApp() && (
                  <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <Button
                      variant="outline"
                      size="sm"
                      className="group relative gap-2 px-4 py-2.5 rounded-xl border border-white/30 dark:border-white/20 bg-gradient-to-br from-white/90 via-white/80 to-white/70 dark:from-gray-800/90 dark:via-gray-800/80 dark:to-gray-800/70 backdrop-blur-md hover:from-white dark:hover:from-gray-700 hover:via-white dark:hover:via-gray-700 hover:to-white dark:hover:to-gray-700 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownload();
                      }}
                    >
                      {/* Shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                      
                      <Download className="h-4 w-4 text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors relative z-10" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-200 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors relative z-10">
                        {t("wizard.download") || t("common.download", { defaultValue: "İndir" })}
                      </span>
                    </Button>
                  </motion.div>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
  );

  // Portal kullanarak document.body'ye render et
  return createPortal(modalContent, document.body);
}
